// SEND-142 (Howard ruled 2026-08-27) — THE RAIL SPLIT. The editor's right
// column was one 300-line block; it is now three panels in three files.
// SAME DATA, SAME BUTTONS, SAME confirm / refuse / pull-in. No new field,
// no new formula, no new call: every figure still arrives from the server's
// own quantities and every decision still lives in the editor.
//
// SEND-211 (Howard ruled 2026-08-14) — GROUPED BY KIND AND COLLAPSED. "The
// marks list is too long down the side. Group by kind. Collapse it. A new
// siding zone lands with the other siding." THE ROWS THEMSELVES DID NOT
// CHANGE — every button, input, receipt and basis line is the one that was
// there. What changed is WHERE a row sits and whether its group is open.
import React from "react";
import { Ban, Check, ChevronDown, ChevronRight, Download, Move, Trash2 } from "lucide-react";

import { GABLE_PITCH_PRESETS, pitchOutOfRange } from "@/lib/gableMath";
import { DORMER, GABLE, J_SIZES, J_TYPES, J_TYPE_LABEL, OPENING_TYPES, kindLabel, markColor } from "./marks";

// SEND-235 — the four opening types come from the ONE shared vocabulary in
// ./marks, so the rail chips and the canvas chips can never drift apart.
const OPENING_TYPE_LABEL = Object.fromEntries(
  OPENING_TYPES.map((o) => [o.key, o.label]));

// SEND-208 — the type as a NAME for the row. Unset says "no type picked".
// SEND-241 — the labels come from the ONE shared J vocabulary in ./marks, the
// same list the rail's Product chips render.
const J_ROW_TYPES = J_TYPE_LABEL;
// SEND-217 — the siding type as a NAME for the row, the same way. Unset says
// "no type picked" — an older zone drawn before the chips is never
// backfilled and never guessed.
const SIDING_ROW_TYPES = { clap: "clap", dutch_lap: "Dutch lap", bb: "B&B",
                           shake: "shake", vertical: "vertical",
                           nickel_gap: "nickel gap" };
// SEND-221 — the SERIES on the row too, because the series and the type
// together are what name a catalog line. Unset says "no series picked".
// SEND-222 — the soffit row names its series and vent the same way.
const SOFFIT_ROW_SERIES = { charter_oak: "Charter Oak",
                            greenbriar: "Greenbriar", two_t: "2T" };
const SIDING_ROW_SERIES = { conquest: "Conquest", coventry: "Coventry",
                            odyssey: "Odyssey", charter_oak: "Charter Oak",
                            vertical_bb: "Vertical B&B", shakes: "Shakes" };

// SEND-211 — HIS ELEVEN GROUPS, IN HIS ORDER, then the three anchors he
// ruled sit at the end so NO MARK EVER VANISHES FROM THE RAIL. One group per
// kind and kinds are never mixed: GABLE and DORMER are their own groups and
// are NOT folded into SIDING. An empty group is hidden — a group appears the
// moment this photo carries its first mark of that kind.
//
// THE HEADER'S FIGURE IS THE SERVER'S OWN, READ ONCE. `qty` here is the
// server's quantities object, which is ALREADY CONFIRMED-MARKS-ONLY, so the
// header cannot drift from the rows under it. Nothing is summed, averaged or
// derived in this file: the editor still measures nothing itself. A kind with
// no confirmed figure prints the em dash — NEVER a 0. The anchors carry no
// figure at all, because a wall base and an eave are anchors and never an LF
// run.
const AREA_KEY = {
  siding_zone: "siding_sqft",
  non_siding_zone: "non_siding_sqft",
  opening: "opening_sqft",
  gable: "gable_sqft",
  dormer: "dormer_face_sqft",
};
const LINEAR_KINDS = ["starter", "fascia", "gutter", "soffit", "j_channel",
  "outside_corner", "inside_corner", "coil", "finish_trim"];
export const MARK_GROUPS = [
  { kind: "siding_zone", label: "SIDING" },
  { kind: "non_siding_zone", label: "NON-SIDING" },
  { kind: "opening", label: "OPENING" },
  { kind: "gable", label: "GABLE" },
  { kind: "dormer", label: "DORMER" },
  { kind: "starter", label: "STARTER" },
  { kind: "fascia", label: "FASCIA" },
  // SEND-298a — the gutter run has its own group, right behind fascia.
  { kind: "gutter", label: "GUTTER" },
  // SEND-298c — the DOWNSPOUT lines he draws on that same step. Its header
  // figure is a COUNT OF STICKS, not feet: one confirmed line, one stick.
  { kind: "downspout", label: "DOWNSPOUT" },
  { kind: "soffit", label: "SOFFIT" },
  { kind: "j_channel", label: "J-CHANNEL" },
  { kind: "outside_corner", label: "OUT CORNER" },
  { kind: "inside_corner", label: "IN CORNER" },
  { kind: "coil", label: "COIL" },
  { kind: "finish_trim", label: "FINISH TRIM" },
  { kind: "wall_base", label: "WALL BASE" },
  { kind: "eave", label: "EAVE" },
  { kind: "scale", label: "SCALE" },
];

export const groupTotal = (kind, qty) => {
  if (AREA_KEY[kind]) {
    const v = qty?.[AREA_KEY[kind]];
    return v == null ? "—" : `${v} ft²`;
  }
  // SEND-298c — THE DOWNSPOUT HEADER IS A STICK COUNT, never feet: "each
  // confirmed downspout line = 1 STICK; do not convert the line's LF into
  // sticks." The server counts the lines; nothing is summed here.
  if (kind === "downspout") {
    const v = qty?.downspout_lines;
    return v == null ? "—" : `${v} STICK`;
  }
  if (LINEAR_KINDS.includes(kind)) {
    const row = (qty?.trim_rows || []).find((r) => r.key === kind);
    return row?.lf == null ? "—" : `${row.lf} LF`;
  }
  return "—";
};

// SEND-157 — WIDTH FIRST, LABELLED. The boxes read left to right the way the
// order reads: WIDTH then HEIGHT. Nothing is flipped for him; the left box IS
// the width.
// SEND-252 (Howard ruled 2026-08-14) — THE SAME TWO BOXES, ON THE OPENINGS
// STEP OF THE WALK: "when he clicks a boxed opening, the right rail shows
// WIDTH, HEIGHT. Inches. Typed tape sizes. The tape wins over the photo-read
// size." So they are lifted into ONE component and rendered in BOTH places —
// this row and the walk rail — and there is no second pair of fields to
// drift. SAME `patchMark`, SAME two fields (`width_in`, `height_in`), and the
// server already prefers a typed size over the read one and already runs the
// plane check off exactly these two numbers. NO NEW PLANE MATH WAS WRITTEN,
// nothing about drawing an opening changed, and DELETE THIS OPENING is
// untouched.
export const OpeningSizeFields = ({ m, patchMark }) => (
  <>
    <label className="flex flex-col gap-0.5" data-testid={`photo-takeoff-width-field-${m.id}`}>
      <span className="text-[8px] font-bold uppercase tracking-wider text-[var(--ink-2)]">Width (in)</span>
      <input defaultValue={m.width_in ?? ""} placeholder="width" inputMode="decimal"
        onBlur={(e) => { const v = parseFloat(e.target.value); if (v > 0 && v !== m.width_in) patchMark(m.id, { width_in: v }); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        className="w-16 border border-[var(--border)] px-1.5 py-1 text-[10px]"
        data-testid="photo-takeoff-width-in" />
    </label>
    <label className="flex flex-col gap-0.5" data-testid={`photo-takeoff-height-field-${m.id}`}>
      <span className="text-[8px] font-bold uppercase tracking-wider text-[var(--ink-2)]">Height (in)</span>
      <input defaultValue={m.height_in ?? ""} placeholder="height" inputMode="decimal"
        onBlur={(e) => { const v = parseFloat(e.target.value); if (v > 0 && v !== m.height_in) patchMark(m.id, { height_in: v }); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        className="w-16 border border-[var(--border)] px-1.5 py-1 text-[10px]"
        data-testid="photo-takeoff-height-in" />
    </label>
    <span className="text-[9px] text-[var(--muted)] w-full">Width × height is the order. Style is a name only.</span>
    <span className="text-[9px] text-[var(--muted)] w-full"
      data-testid={`photo-takeoff-opening-tape-basis-${m.id}`}>
      Inches, off your tape. A typed size wins over the size read off this photo, and it is what the plane box asks for.
    </span>
    {/* SEND-254 — COUNT and STYLE sit next to WIDTH and HEIGHT, in the same
        one component, so the walk's openings step and the grouped rail ask
        for them the same way. */}
    <OpeningCountAndStyle m={m} patchMark={patchMark} />
  </>
);

// SEND-298b (Howard ruled 2026-08-14) — THE TWO COUNTS THAT RIDE A GUTTER
// LINE. "Do not add a Downspout step. Downspouts are part of the Gutter step:
// after the two-tap gutter line, a count of drops for that run." Elbows are
// the same — a count on the SAME step, and HIS COUNT WINS. Both are whole
// counts he types; neither is derived from the feet and no LF becomes a stick.
// SEND-298b — the gutter run's typed ELBOW count, asked on the GUTTER step.
// SEND-298c TOOK THE DROPS BOX OFF: "he does not want to type drops — a
// downspout is DRAWN like a gutter." So one box is left, and it is the one he
// kept. His elbow count wins and is never the downspouts doubled.
export const GutterCountFields = ({ m, patchMark }) => (
  <>
    <label className="flex flex-col gap-0.5" data-testid={`photo-takeoff-elbows-field-${m.id}`}>
      <span className="text-[8px] font-bold uppercase tracking-wider text-[var(--ink-2)]">Elbows</span>
      <input defaultValue={m.elbows ?? ""} placeholder="elbows" inputMode="numeric"
        onBlur={(e) => { const v = parseInt(e.target.value, 10); if (v >= 0 && v !== m.elbows) patchMark(m.id, { elbows: v }); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        className="w-16 border border-[var(--border)] px-1.5 py-1 text-[10px]"
        data-testid={`photo-takeoff-elbows-${m.id}`} />
    </label>
  </>
);

export const GUTTER_COUNTS_BASIS = "The ELBOW count is yours, per run, and lands as you type it — never the downspouts doubled. DOWNSPOUTS ARE DRAWN, NOT TYPED: switch to the DOWNSPOUT tool above and draw each one top to bottom. Every confirmed downspout line is 1 STICK on Downspout 6\" and fills Pipe Clips at 2 each (drop height was never measured), and the sealant with the end caps. The feet on a downspout line are its label only.";

export const OPENING_STYLES = [
  "Double hung", "2-lite slider", "3-lite slider", "Casement", "Awning",
  "Picture", "Hopper", "Fixed", "Bay", "Sliding glass",
];

// SEND-254 (Howard ruled 2026-08-14) — OPENING COUNT AND WINDOW STYLE. TAG
// ONLY. NO MONEY. COUNT defaults to 1 and is TYPED: "a twin mulled unit is one
// box with count 2", and nothing guesses it from a style, a name, a width or
// an aspect. STYLE is OPTIONAL and a PICKER of the ten names above — skip it
// and the opening stays WINDOW. Both ride the mark: no windows-estimate line
// is written, no price moves, and the sheet's own opening count is still the
// number of confirmed boxes. GARAGE DOOR AND ENTRY DOOR STAY AS THEY ARE —
// neither control is offered on them.
export const OpeningCountAndStyle = ({ m, patchMark }) => {
  const style = m.style || "";
  // GARAGE DOOR AND ENTRY DOOR STAY AS THEY ARE: neither control is offered
  // on them, and no early return is taken — this is one component that
  // renders the door case as nothing at all.
  const doorish = !!m.opening_type && m.opening_type !== "window";
  return (
    <>
      {doorish ? null : !m.opening_type ? (
        <span className="text-[9px] text-[var(--muted)] w-full"
          data-testid={`photo-takeoff-opening-count-needs-type-${m.id}`}>
          Pick WINDOW as the type and its COUNT and STYLE ask for your word here. Nothing is tagged for you.
        </span>
      ) : (
        <>
      <label className="flex flex-col gap-0.5" data-testid={`photo-takeoff-count-field-${m.id}`}>
        <span className="text-[8px] font-bold uppercase tracking-wider text-[var(--ink-2)]">Count</span>
        <input key={`count-${m.id}-${m.count ?? 1}`}
          defaultValue={m.count ?? 1} placeholder="1" inputMode="numeric"
          onBlur={(e) => { const v = parseInt(e.target.value, 10); if (v >= 1 && v !== (m.count ?? 1)) patchMark(m.id, { count: v }); }}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          className="w-14 border border-[var(--border)] px-1.5 py-1 text-[10px]"
          data-testid="photo-takeoff-opening-count" />
      </label>
      <div className="flex flex-col gap-0.5 w-full" data-testid={`photo-takeoff-style-field-${m.id}`}>
        <span className="text-[8px] font-bold uppercase tracking-wider text-[var(--ink-2)]">Style — optional</span>
        <div className="flex flex-wrap gap-1">
          {OPENING_STYLES.map((s) => (
            <button key={s} type="button"
              onClick={() => patchMark(m.id, { style: style === s ? "" : s })}
              title={style === s
                ? `Tap again to take the style back off — the opening stays WINDOW`
                : `Tag this window ${s} — a name only: it moves no size, no count and no money`}
              className={`px-1.5 py-0.5 border text-[9px] uppercase tracking-wider font-bold ${
                style === s
                  ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                  : "border-[var(--border)] hover:bg-[var(--surface-muted)]"}`}
              data-testid={`photo-takeoff-opening-style-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <span className="text-[9px] text-[var(--muted)] w-full"
        data-testid={`photo-takeoff-opening-count-note-${m.id}`}>
        {(m.count ?? 1) > 1
          ? `Tagged ${m.count} on this one box — a tag only: it is not multiplied into the opening count, the J blocks, any ft² or any money.`
          : "One box is one opening. A twin mulled unit is this one box with count 2 — typed by you, never guessed from the style, the name or the width."}
        {style ? "" : " No style picked: this opening stays WINDOW."}
      </span>
        </>
      )}
    </>
  );
};

const MarkRow = ({
  m, selectedId, setSelectedId, busy, products, productsNote,
  sqftOf, qtyCell, receiptFor, gDims, ft2,
  patchMark, delMark, toggleSymmetric, applyGablePitch,
}) => {
  const a = sqftOf(m);
  return (
                  <div
                    className={`border p-1.5 mb-1 cursor-pointer ${m.id === selectedId ? "border-[var(--ai)]" : "border-[var(--border)]"}`}
                    onClick={() => setSelectedId(m.id === selectedId ? null : m.id)}
                    data-testid={`photo-takeoff-mark-row-${m.id}`}>
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[11px] font-bold" style={{ color: markColor(m) }}>
                        {kindLabel(m)}
                        {/* SEND-208 — the J shape and the picked TYPE print
                            on the row. Both are names: neither changes the
                            length and no type is guessed when none is
                            picked. */}
                        {m.kind === "j_channel" ? (
                          <span className="font-normal text-[var(--ink-2)]"
                            data-testid={`photo-takeoff-j-row-type-${m.id}`}>
                            {` · ${m.shape === "rect" ? "box" : m.shape === "poly" ? "polygon" : "line"} · ${J_ROW_TYPES[m.j_type] || "no type picked"}`}
                            {/* SEND-223 — the SIZE prints beside the use. No
                                size picked says so: it is never guessed. */}
                            {` · ${m.j_size ? `${m.j_size}"` : "no size picked"}`}
                          </span>
                        ) : null}
                        {m.kind === "soffit" ? (
                          <span className="font-normal text-[var(--ink-2)]"
                            data-testid={`photo-takeoff-soffit-row-series-${m.id}`}>
                            {` · ${SOFFIT_ROW_SERIES[m.soffit_series] || "no series picked"}`}
                            {` ${m.soffit_vent || "no vent picked"}`}
                          </span>
                        ) : null}
                        {/* SEND-217 — the siding type prints on the row. A
                            name: it changes no ft² and no money. */}
                        {m.kind === "siding_zone" ? (
                          <span className="font-normal text-[var(--ink-2)]"
                            data-testid={`photo-takeoff-siding-row-type-${m.id}`}>
                            {` · ${SIDING_ROW_SERIES[m.siding_series] || "no series picked"}`}
                            {` ${SIDING_ROW_TYPES[m.siding_type] || "no type picked"}`}
                            {m.siding_width ? ` ${m.siding_width}"` : ""}
                          </span>
                        ) : null}
                      </span>
                      <span className="text-[10px] font-bold">
                        {qtyCell(m, a)}
                      </span>
                    </div>
                    {/* SEND-165 item 2 — NAME THE HAND BOXES. A typed name
                        rides the box he drew, so the sheet stops calling it
                        "opening". A NAME CHANGES ONLY THE NAME. */}
                    {m.kind === "opening" && (
                      <div className="mt-0.5" onClick={(e) => e.stopPropagation()}>
                        <input type="text" defaultValue={m.name || ""}
                          placeholder="name this box (yours, like the size)"
                          data-testid={`photo-takeoff-opening-name-input-${m.id}`}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v !== (m.name || "")) patchMark(m.id, { name: v });
                          }}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          className="w-full px-1 py-0.5 text-[10px] font-bold bg-[var(--surface)] border border-[var(--border)] focus:border-[var(--ai)] outline-none" />
                        {m.name ? (
                          <div className="text-[8px] uppercase tracking-wider text-[var(--muted)]"
                            data-testid={`photo-takeoff-opening-name-basis-${m.id}`}>
                            typed{m.name_typed_by ? ` · ${m.name_typed_by}` : ""} · a name changes only the name
                          </div>
                        ) : null}
                      </div>
                    )}
                    <div className="text-[9px] uppercase tracking-wider font-bold" style={{ color: m.status === "confirmed" ? "var(--success)" : m.status === "refused" ? "var(--muted)" : "var(--warning-text)" }}>
                      {m.status}
                    </div>
                    {/* PROVENANCE NEVER LAUNDERS — the badge says which the
                        mark carries, and a confirm does not rewrite it. */}
                    <div className="text-[9px] font-bold uppercase tracking-wider" data-testid={`photo-takeoff-origin-${m.id}`}
                      style={{ color: m.origin === "ai_proposal" ? "var(--ai)" : m.status === "confirmed" && m.confirmed_after_ai_read ? "var(--success)" : "var(--warning-text)" }}>
                      {m.origin === "ai_proposal" ? "AI PROPOSAL"
                        : m.confirmed_after_ai_read ? "EVIDENCE"
                          : m.status === "confirmed" ? "GUIDANCE-CONFIRMED" : "GUIDANCE"}
                      {m.origin === "imported_annotation" ? " · imported" : ""}
                    </div>
                    {(m.confirmed_basis || m.basis) && (
                      <div className="text-[9px] text-[var(--muted)] leading-snug" data-testid={`photo-takeoff-basis-${m.id}`}>
                        {m.confirmed_basis || m.basis}
                      </div>
                    )}
                    {/* SEND-153 — THE HEADLINE ON AN OPENING IS ITS OUTSIDE
                        DIMENSION IN INCHES, WIDTH FIRST. A size he typed
                        wins; the photo-derived figure stays visible as the
                        read. No scale on the photo → the sentence, never a
                        number. The ft² is the second line. */}
                    {m.kind === "opening" && m.size && (
                      <div className="mt-0.5" data-testid={`photo-takeoff-opening-size-${m.id}`}>
                        {m.size.label ? (
                          <div className="text-[12px] font-bold text-[var(--ink)] leading-tight">
                            {m.size.label}
                            <span className="ml-1 text-[8px] font-bold uppercase tracking-wider text-[var(--ink-2)]">
                              {m.size.typed_by === "you" ? "typed · the order"
                                : m.size.typed_by === "the AI read" ? "AI's stated size · confirm or type yours"
                                  : "read off this photo"}
                              {` · ${m.size.axis}`}
                            </span>
                          </div>
                        ) : (
                          <div className="text-[10px] font-bold text-[var(--warning-text)] leading-snug"
                            data-testid={`photo-takeoff-opening-size-refusal-${m.id}`}>
                            {m.size.refusal}
                          </div>
                        )}
                        {/* SEND-154 — THE AXIS IS NAMED IN WORDS. The server
                            writes this whole sentence; nothing here composes
                            two bare numbers. */}
                        {m.size.typed_width_in && m.size.typed_height_in && m.size.read_line && (
                          <div className="text-[9px] text-[var(--ink-2)]" data-testid={`photo-takeoff-opening-read-${m.id}`}>
                            {m.size.read_line}
                          </div>
                        )}
                        {/* SEND-179 — THE STYLE/NAME FLAG, AND IT OUTRANKS
                            THE SIDEWAYS NOTE. The server decides the
                            precedence and sends only the flag whose premise
                            holds; nothing here picks between them. */}
                        {m.size.style_name && (
                          <div className="mt-0.5 text-[9px] font-bold leading-snug text-[var(--warning-text)]"
                            data-testid={`photo-takeoff-opening-style-name-${m.id}`}>
                            {m.size.style_name.flag}
                            <span className="block font-normal text-[var(--ink-2)]">{m.size.style_name.basis}</span>
                          </div>
                        )}
                        {/* SEND-159 — A NOTE, NEVER A GATE. The size still
                            prints and still lands on the order sheet; nothing
                            here swaps a stored value. */}
                        {m.size.orientation && (
                          <div className="mt-0.5 text-[9px] font-bold leading-snug text-[var(--warning-text)]"
                            data-testid={`photo-takeoff-opening-orientation-${m.id}`}>
                            {m.size.orientation.flag}
                            <span className="block font-normal text-[var(--ink-2)]">{m.size.orientation.basis}</span>
                          </div>
                        )}
                        <div className="text-[9px] text-[var(--ink-2)]">
                          {sqftOf(m) != null ? `${sqftOf(m)} ft² · for the siding deduction only` : ""}
                        </div>
                      </div>
                    )}
                    {/* SEND-155 — WHERE THE READ STATES TWO SIZES FOR ONE
                        WINDOW, BOTH ARE PRINTED AND NEITHER IS PICKED. The
                        server names each place; nothing here averages or
                        chooses. */}
                    {m.kind === "opening" && m.ai_sizes && (m.ai_sizes.schedule || m.ai_sizes.detail) && (
                      <div className="mt-0.5" data-testid={`photo-takeoff-opening-ai-sizes-${m.id}`}>
                        {m.ai_sizes.schedule && (
                          <div className="text-[9px] text-[var(--ink-2)]" data-testid={`photo-takeoff-opening-ai-schedule-${m.id}`}>
                            AI schedule: {m.ai_sizes.schedule}
                          </div>
                        )}
                        {m.ai_sizes.detail && (
                          <div className="text-[9px] text-[var(--ink-2)]" data-testid={`photo-takeoff-opening-ai-detail-${m.id}`}>
                            AI detail: {m.ai_sizes.detail}
                          </div>
                        )}
                        <div className={`text-[9px] leading-snug ${m.ai_sizes.disagree ? "font-bold text-[var(--warning-text)]" : "text-[var(--muted)]"}`}
                          data-testid={`photo-takeoff-opening-ai-sizes-note-${m.id}`}>
                          {m.ai_sizes.note}
                        </div>
                        {/* SEND-162 — the card NAMES which run it resolved
                            through: live, or which frozen copy and its date. */}
                        {m.ai_sizes.resolution_note && (
                          <div className="text-[9px] text-[var(--muted)]"
                            data-testid={`photo-takeoff-opening-ai-run-resolution-${m.id}`}>
                            {m.ai_sizes.resolution_note}
                          </div>
                        )}
                      </div>
                    )}
                    {m.style && <div className="text-[9px] text-[var(--ink-2)]">{m.style}{m.width_in ? ` · ${m.width_in}" w` : ""}{m.height_in ? ` × ${m.height_in}" h` : ""}</div>}
                    {m.product && (
                      <div className="text-[9px] text-[var(--ink-2)]" data-testid={`photo-takeoff-product-of-${m.id}`}>
                        product: {m.product}
                        {m.confirmed_under_product && m.confirmed_under_product !== m.product
                          ? ` · confirmed under ${m.confirmed_under_product} — the geometry did not change; the output did` : ""}
                      </div>
                    )}
                    {m.refused_reason && <div className="text-[9px] text-[var(--muted)] leading-snug">{m.refused_reason}</div>}
                    {/* SEND-140 — THE REFUSAL RECEIPT. One contractor
                        sentence, written by the server from the ACTUAL
                        missing field, saying what to tape. A measured
                        gable and a counted cheek carry none. */}
                    {receiptFor(m.id) && (
                      <div className="mt-0.5 text-[10px] font-bold text-[var(--warning-text)] leading-snug"
                        data-testid={`photo-takeoff-receipt-${m.id}`}>
                        {receiptFor(m.id)}
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-1">
                      <button type="button" onClick={(e) => { e.stopPropagation(); patchMark(m.id, { status: "confirmed" }); }}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 border border-[var(--success)] text-[var(--success)] text-[9px] font-bold uppercase"
                        data-testid={`photo-takeoff-confirm-${m.id}`}><Check className="w-3 h-3" /> confirm</button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); patchMark(m.id, { status: "refused", refused_reason: "refused by the contractor" }); }}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 border border-[var(--border)] text-[var(--muted)] text-[9px] font-bold uppercase"
                        data-testid={`photo-takeoff-refuse-${m.id}`}><Ban className="w-3 h-3" /> refuse</button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedId(m.id); }}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 border border-[var(--border)] text-[var(--ink-2)] text-[9px] font-bold uppercase"
                        data-testid={`photo-takeoff-adjust-${m.id}`}><Move className="w-3 h-3" /> adjust</button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); delMark(m.id); }}
                        className="ml-auto text-[var(--danger)]" data-testid={`photo-takeoff-delete-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    {m.id === selectedId && (
                      <div className="mt-1.5 pt-1.5 border-t border-[var(--border)]" onClick={(e) => e.stopPropagation()}>
                        {/* SEND-241 (Howard ruled 2026-08-14) — THE J'S
                            PRODUCT AND SIZE, ON ITS OWN ROW IN THE RAIL.
                            "Do not put type chips on the photo. They stay in
                            the rail." Same `patchMark`, same two fields as the
                            chips SEND-208/223 put on the photo — and the
                            mark's colour follows its PRODUCT, so re-naming it
                            here re-colours it on every photo. NAMES ONLY: no
                            length changes, no money is written, and clearing
                            one is honest — nothing is backfilled onto a J. */}
                        {m.kind === "j_channel" ? (
                          <div className="mb-1.5" data-testid={`photo-takeoff-j-row-chips-${m.id}`}>
                            <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-1">Product</div>
                            <div className="flex flex-wrap gap-1">
                              {J_TYPES.map((t) => (
                                <button key={t.key} type="button" disabled={busy}
                                  onClick={(e) => { e.stopPropagation(); patchMark(m.id, { j_type: m.j_type === t.key ? "" : t.key }); }}
                                  title={`Name this J ${t.label} — a name only, it changes no length and no money; its colour on the photo follows it`}
                                  className={`px-2 py-1 text-[10px] font-bold uppercase border ${m.j_type === t.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
                                  data-testid={`photo-takeoff-j-type-${t.key}-${m.id}`}>
                                  {t.label}
                                </button>
                              ))}
                            </div>
                            <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mt-1.5 mb-1">Size</div>
                            <div className="flex flex-wrap gap-1">
                              {J_SIZES.map((s) => (
                                <button key={s.key} type="button" disabled={busy}
                                  onClick={(e) => { e.stopPropagation(); patchMark(m.id, { j_size: m.j_size === s.key ? "" : s.key }); }}
                                  title={`Name this J ${s.label} — with its use it names one catalog line at APPLY; the feet are already measured either way`}
                                  className={`px-2 py-1 text-[10px] font-bold uppercase border ${m.j_size === s.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
                                  data-testid={`photo-takeoff-j-size-${s.key === "3/4" ? "34" : "12"}-${m.id}`}>
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        {m.kind === "gable" || m.kind === "dormer" ? (
                          /* SEND-139 — THE ANNOTATOR'S OWN FIELDS, PORTED.
                             Gable: symmetric + pitch (preset or typed).
                             Dormer: typed depth for the cheeks. Nothing
                             new was invented for either tool. */
                          (() => {
                            const d = gDims(m);
                            if (m.kind === "gable") {
                              return (
                                <div className="space-y-1">
                                  <div className="text-[10px] font-bold" style={{ color: GABLE }} data-testid={`photo-takeoff-gable-dims-${m.id}`}>
                                    {d && d.baseFt !== undefined
                                      ? `${d.baseFt.toFixed(1)} ft × ${d.riseFt.toFixed(1)} ft rise · ½ × w × rise = ${ft2(d.grossAreaFt)}`
                                      : "no scale on this photo — width and rise have no feet, so there is no area (refused, not 0)"}
                                    {d?.pitch != null ? ` · pitch ${d.pitch}/12` : ""}
                                  </div>
                                  {d && pitchOutOfRange(d.pitch) && (
                                    <div className="text-[9px] font-bold text-[var(--warning-text)]" data-testid={`photo-takeoff-gable-pitch-warning-${m.id}`}>
                                      pitch {d.pitch}/12 is outside the usual 3/12–18/12 range — check the tapped points
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <label className="flex items-center gap-1 text-[10px]">
                                      <input type="checkbox" checked={!!m.symmetric}
                                        onChange={() => toggleSymmetric(m)}
                                        data-testid={`photo-takeoff-gable-symmetric-${m.id}`} />
                                      Symmetric gable
                                    </label>
                                    <select value={GABLE_PITCH_PRESETS.includes(m.pitch_set) ? String(m.pitch_set) : ""}
                                      onChange={(e) => { const v = Number(e.target.value); if (v) applyGablePitch(m, v); }}
                                      className="text-[10px] border border-[var(--border)] px-1 py-0.5"
                                      title="Selecting a pitch moves the peak (rise = base/2 × pitch/12). Dragging the peak re-derives the pitch."
                                      data-testid={`photo-takeoff-gable-pitch-${m.id}`}>
                                      <option value="">pitch {d?.pitch != null ? `${d.pitch}/12` : "—"}</option>
                                      {GABLE_PITCH_PRESETS.map((v) => <option key={v} value={v}>{v}/12</option>)}
                                    </select>
                                    <input type="number" min="1" max="24" step="0.5" placeholder="custom"
                                      className="w-16 text-[10px] border border-[var(--border)] px-1 py-0.5"
                                      onKeyDown={(e) => { if (e.key === "Enter") { const v = Number(e.target.value); if (v > 0) applyGablePitch(m, v); } }}
                                      data-testid={`photo-takeoff-gable-pitch-custom-${m.id}`} />
                                  </div>
                                  <div className="text-[9px] text-[var(--muted)]">
                                    Moving the peak or the eaves is a geometry change — a confirmed gable goes back to provisional and is re-confirmed.
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div className="space-y-1">
                                <div className="text-[10px] font-bold" style={{ color: DORMER }} data-testid={`photo-takeoff-dormer-dims-${m.id}`}>
                                  {d && d.widthFt !== undefined
                                    ? `${d.widthFt.toFixed(1)} ft × ${d.heightFt.toFixed(1)} ft = ${ft2(d.grossAreaFt)} face`
                                    : "no scale on this photo — no feet, no area (refused, not 0)"}
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <label className="text-[10px] text-[var(--muted)] font-bold" htmlFor={`ptd-${m.id}`}>Depth (ft)</label>
                                  <input id={`ptd-${m.id}`} type="number" min="0" step="0.1" inputMode="decimal"
                                    defaultValue={m.depth_ft ?? ""}
                                    onBlur={(e) => { const v = parseFloat(e.target.value); if (v > 0 && v !== m.depth_ft) patchMark(m.id, { depth_ft: v }); }}
                                    className="w-16 text-[10px] border border-[var(--border)] px-1 py-0.5"
                                    data-testid={`photo-takeoff-dormer-depth-${m.id}`} />
                                  {m.depth_ft && d?.heightFt !== undefined ? (
                                    <span className="text-[10px] font-bold" style={{ color: DORMER }} data-testid={`photo-takeoff-dormer-cheeks-${m.id}`}>
                                      + cheeks 2 × {d.heightFt.toFixed(1)}×{Number(m.depth_ft).toFixed(1)} = {ft2(2 * d.heightFt * Number(m.depth_ft))}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] font-bold text-[var(--warning-text)]" data-testid={`photo-takeoff-dormer-cheeks-refused-${m.id}`}>
                                      cheeks REFUSED — depth is measured on the roof, never read off the photo. No default depth.
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })()
                        ) : m.kind === "opening" ? (
                          <div className="flex flex-wrap items-center gap-1">
                            {/* SEND-234 — WHICH KIND IS IT? Four types, asked
                                AFTER the box is drawn. The pick is a NAME on
                                that mark: it moves no ft², no LF, no
                                deduction and no count, it builds no window
                                line and no door line, and it seeds nothing
                                into J. Tapping the picked one clears it, and
                                a box with NO TYPE STAYS AN OPENING.
                                SEND-242 (Howard ruled 2026-08-14) — THIS ROW
                                IS NOW THE ONLY PLACE THE TYPE IS NAMED: "Type
                                chips come OFF the selected box. They live on
                                that opening's row in the rail. Changing
                                Product there recolors the mark." So the group
                                carries his PRODUCT label — the same word the
                                walk rail uses — and the four chips are the
                                ones that left the photo. DELETE stays on the
                                box. No second group was added: this is the one
                                that was already here, labeled. */}
                            <div className="w-full"
                              data-testid={`photo-takeoff-opening-row-chips-${m.id}`}>
                              <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted)] mb-1">Product</div>
                            <div className="flex flex-wrap items-center gap-1 w-full"
                              data-testid={`photo-takeoff-rail-opening-type-row-${m.id}`}>
                              {OPENING_TYPES.map((o) => (
                                <button key={o.key} type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    patchMark(m.id, { opening_type: m.opening_type === o.key ? "" : o.key });
                                  }}
                                  title={`${o.label} — a NAME on this box, and a name only. It changes no size, no ft², no deduction, no count and no money, and it is not the STANDARD / ARCHITECTURAL vinyl tap`}
                                  className={`px-2 py-1 text-[10px] font-bold uppercase border ${m.opening_type === o.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "text-[var(--ink-2)] border-[var(--border)]"}`}
                                  data-testid={`photo-takeoff-rail-opening-type-${o.key}-${m.id}`}>
                                  {o.label}
                                </button>
                              ))}
                              <span className="text-[9px] text-[var(--muted)] w-full"
                                data-testid={`photo-takeoff-rail-opening-type-basis-${m.id}`}>
                                {m.opening_type
                                  ? `${OPENING_TYPE_LABEL[m.opening_type]}${m.opening_type_set_by ? ` · ${m.opening_type_set_by}` : ""} — a type changes only the type`
                                  : "no type picked — this box stays an opening. Nothing is guessed"}
                              </span>
                            </div>
                            </div>
                            <input defaultValue={m.style || ""} placeholder="window style"
                              onBlur={(e) => { const v = e.target.value.trim(); if (v !== (m.style || "")) patchMark(m.id, { style: v }); }}
                              className="flex-1 min-w-[110px] border border-[var(--border)] px-1.5 py-1 text-[10px]"
                              data-testid="photo-takeoff-style-input" />
                            <OpeningSizeFields m={m} patchMark={patchMark} />
                          </div>
                        ) : (
                          <div>
                            <select value={m.product || ""} disabled={products.length === 0}
                              onChange={(e) => patchMark(m.id, { product: e.target.value })}
                              className="w-full border border-[var(--border)] px-1.5 py-1 text-[10px] disabled:opacity-50"
                              data-testid="photo-takeoff-product-select">
                              <option value="">{products.length ? "— no product assigned —" : "— none on this job —"}</option>
                              {products.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                            </select>
                            <span className="text-[9px] text-[var(--muted)]">
                              {products.length
                                ? "Changing the product does NOT redraw or unconfirm the zone — the swap is recorded with the ft² at that moment."
                                : (productsNote || "no body-siding product on this job yet")}
                            </span>
                            {(m.product_history || []).length > 0 && (
                              <div className="mt-1 text-[9px] text-[var(--muted)]" data-testid={`photo-takeoff-product-history-${m.id}`}>
                                {m.product_history.map((h, i) => (
                                  <div key={i}>{h.from || "—"} → {h.to || "—"} · {h.sqft_at_swap ?? "—"} ft² · {String(h.at).slice(0, 16).replace("T", " ")} · {h.by || "unknown"}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
  );
};

export const MarksPanel = ({
  marks, qty, selectedId, setSelectedId, busy, products, productsNote,
  sqftOf, qtyCell, receiptFor, gDims, ft2,
  patchMark, delMark, importAnnotations, toggleSymmetric, applyGablePitch,
}) => {
  // SEND-211 — EVERYTHING STARTS COLLAPSED, and SELECTING A MARK OPENS ITS
  // GROUP so the row he is working on is never behind a closed header. The
  // header still toggles either way — he can shut the group he is in. The
  // state is this editor instance's own and is remembered NOWHERE: re-opening
  // the photo opens collapsed again, which is the point of collapsing.
  const [open, setOpen] = React.useState([]);
  const selKind = (marks.find((m) => m.id === selectedId) || {}).kind;
  React.useEffect(() => {
    if (selKind) setOpen((o) => (o.includes(selKind) ? o : [...o, selKind]));
  }, [selectedId, selKind]);
  const groups = MARK_GROUPS
    .map((g) => ({ ...g, rows: marks.filter((m) => m.kind === g.kind) }))
    .filter((g) => g.rows.length > 0);
  return (
            <div className="p-3 flex-1">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--muted)]">Marks ({marks.length})</div>
                {/* SEND-151 — in the MEASURE WITHOUT AI door there is no
                    annotate step to pull from, so this button is not there. */}
                {importAnnotations && (
                  <button type="button" onClick={importAnnotations} disabled={busy}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--border)] text-[9px] font-bold uppercase text-[var(--ink-2)] disabled:opacity-50"
                    data-testid="photo-takeoff-import-btn"><Download className="w-3 h-3" /> pull in what I already drew</button>
                )}
              </div>
              {marks.length === 0 && (
                <div className="text-[11px] text-[var(--muted)] italic" data-testid="photo-takeoff-marks-empty">
                  No marks on this photo yet — pick a tool and trace the wall.
                </div>
              )}
              {/* SEND-211 — ONE GROUP PER KIND, IN HIS ORDER. A newly drawn
                  mark is appended INSIDE ITS OWN KIND GROUP — siding with
                  siding, J with J — because each group keeps the server's own
                  order for its kind. It never lands at the bottom of the
                  whole list again. */}
              <div data-testid="photo-takeoff-mark-groups">
                {groups.map((g) => {
                  const isOpen = open.includes(g.kind);
                  return (
                    <div key={g.kind} className="mb-1 border border-[var(--border)]"
                      data-testid={`photo-takeoff-group-${g.kind}`}>
                      <button type="button"
                        onClick={() => setOpen((o) => (o.includes(g.kind)
                          ? o.filter((k) => k !== g.kind) : [...o, g.kind]))}
                        className="w-full flex items-center gap-1.5 px-1.5 py-1 bg-[var(--surface-muted)] text-left"
                        title={isOpen ? "collapse this group — nothing is deleted and no quantity moves" : "open this group"}
                        data-testid={`photo-takeoff-group-header-${g.kind}`}>
                        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        <span className="w-2.5 h-2.5 flex-shrink-0" style={{ background: markColor(g.rows[0]) }} />
                        <span className="flex-1 text-[10px] font-bold uppercase tracking-wider"
                          data-testid={`photo-takeoff-group-label-${g.kind}`}>{g.label}</span>
                        <span className="text-[9px] font-bold text-[var(--muted)]"
                          data-testid={`photo-takeoff-group-count-${g.kind}`}>{g.rows.length}</span>
                        <span className="text-[10px] font-bold"
                          title="confirmed marks only — the server's own figure for this kind"
                          data-testid={`photo-takeoff-group-total-${g.kind}`}>{groupTotal(g.kind, qty)}</span>
                      </button>
                      {isOpen && (
                        <div className="p-1" data-testid={`photo-takeoff-group-rows-${g.kind}`}>
                          {g.rows.map((m) => (
                            <MarkRow key={m.id} m={m} selectedId={selectedId}
                              setSelectedId={setSelectedId} busy={busy}
                              products={products} productsNote={productsNote}
                              sqftOf={sqftOf} qtyCell={qtyCell}
                              receiptFor={receiptFor} gDims={gDims} ft2={ft2}
                              patchMark={patchMark} delMark={delMark}
                              toggleSymmetric={toggleSymmetric}
                              applyGablePitch={applyGablePitch} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
  );
};

export default MarksPanel;
